import { resolve } from "../../../api/endPoints";
import {
  IMAGE,
  PDF,
  VIDEO,
} from "../../../components/lomavisCreator/config/constants";
import { VideoThumbnailGenerator } from "browser-video-thumbnail-generator";
import axiosClient from "../../../utils/axios";
import { PDFDocument } from "pdf-lib";
import { useSelector } from "react-redux";
import { lomavisCreatorRootState } from "../../../pages/lomavisCreator";
import {
  plugin_crop,
  plugin_finetune,
  plugin_sticker,
  plugin_filter,
  plugin_annotate,
  plugin_fill,
  plugin_decorate,
  plugin_redact,
  plugin_frame,
} from "@pqina/pintura";

import {
  LocaleCore as LocaleCoreDe,
  LocaleCrop as LocaleCropDe,
  LocaleFinetune as LocaleFinetuneDe,
  LocaleFilter as LocaleFilterDe,
  LocaleAnnotate as LocaleAnnotateDe,
  LocaleDecorate as LocaleDecorateDe,
  LocaleRedact as LocaleRedactDe,
  LocaleMarkupEditor as LocaleMarkupEditorDe,
  LocaleSticker as LocaleStickerDe,
  LocaleFill as LocaleFillDe,
  LocaleTrim as LocaleTrimDe,
  LocaleResize as LocaleResizeDe,
  LocaleFrame as LocaleFrameDe,
} from "@pqina/pintura/locale/de_DE";
import {
  LocaleCore as LocaleCoreEn,
  LocaleCrop as LocaleCropEn,
  LocaleFinetune as LocaleFinetuneEn,
  LocaleFilter as LocaleFilterEn,
  LocaleAnnotate as LocaleAnnotateEn,
  LocaleDecorate as LocaleDecorateEn,
  LocaleRedact as LocaleRedactEn,
  LocaleMarkupEditor as LocaleMarkupEditorEn,
  LocaleSticker as LocaleStickerEn,
  LocaleFill as LocaleFillEn,
  LocaleTrim as LocaleTrimEn,
  LocaleResize as LocaleResizeEn,
  LocaleFrame as LocaleFrameEn,
} from "@pqina/pintura/locale/en_GB";

export const getMediaType = (file: any) => {
  const fileName = file?.name?.toLowerCase?.() || "";
  const fileType = file?.type?.toLowerCase?.() || "";
  const isHeifImage =
    [
      "image/heic",
      "image/heic-sequence",
      "image/heif",
      "image/heif-sequence",
    ].includes(fileType) ||
    [".heic", ".heif", ".heics", ".heifs"].some((extension) =>
      fileName.endsWith(extension)
    );

  if (fileType.includes("image") || isHeifImage) {
    return IMAGE;
  } else if (
    fileType.includes("video") ||
    fileName.endsWith(".mkv") ||
    fileName.endsWith(".mp4") ||
    fileName.endsWith(".avi")
  ) {
    return VIDEO;
  } else if (fileType.includes("pdf")) {
    return PDF;
  }
  return null;
};

export const isMediaImage = (file: any) => {
  return getMediaType(file) === IMAGE;
};

export const isMediaVideo = (file: any) => {
  return getMediaType(file) === VIDEO;
};

export const isMediaPdf = (file: any) => {
  return getMediaType(file) === PDF;
};

export const getVideoThumbnail = async (file) => {
  try {
    const videoSrc = URL.createObjectURL(file);
    const generator = new VideoThumbnailGenerator(videoSrc);
    const { thumbnail } = await generator.getThumbnail();
    return thumbnail;
  } catch (error) {
    console.log("An error occured", error.message, error.code, error.type);
    // If an error occurs, reject the promise with the error message
    return "";
  }
};

export const generateFirebaseUploadUrl = async (
  data: any,
  params: any,
  errorCallback: any = () => {},
  successCallback: any = () => {},
  recaptchaToken: string
) => {
  console.log("sent data", data);

  const url = resolve("create_post_media", {}, { ...params });
  const stringifiedData = JSON.stringify({ ...data });
  console.log("new url", url);

  try {
    // Parse stringifiedData back to an object
    const parsedData = JSON.parse(stringifiedData);

    const res = await axiosClient.post(url, {
      ...parsedData,
      recaptcha: recaptchaToken,
    });

    console.log("upload preset data", recaptchaToken);
    if (!res?.data) {
      errorCallback();
    }

    return res?.data;
  } catch (err: any) {
    console.log(err);

    errorCallback(err.response.data);
  }
};

export const postExternalCollectedData = async (
  data: any,
  params: any,
  errorCallback: any = () => {},
  successCallback: any = () => {},
  submitUrl?: string
) => {
  console.log("sent data", data);

  const url = submitUrl || resolve("external_content_collect", {}, { ...params });
  const stringifiedData = JSON.stringify({ ...data });
  console.log("new url", url);

  try {
    // Parse stringifiedData back to an object
    const parsedData = JSON.parse(stringifiedData);

    const res = await axiosClient.post(url, {
      ...parsedData,
    });

    console.log("upload preset data", parsedData);
    successCallback();
    return res.data;
  } catch (err: any) {
    console.log(err);
    errorCallback(err.response.data);
  }
};

export const getPlannedPost = async (
  startDate: string,
  endDate: string,
  slug: string,
  errorCallback: any = () => {},
  successCallback: any = () => {}
) => {
  console.log("sent data", startDate, endDate);
  const url = resolve(
    "planned_posts",
    {},
    { start: startDate, end: endDate, location__slug: slug }
  );

  try {
    const res = await axiosClient.get(url);
    console.log("response data", res);
    return res.data;
  } catch (err: any) {
    console.log(err);
    errorCallback(err.response.data);
  }
};

export const getPdfPageCount = async (file) => {
  if (file && file.type === "application/pdf") {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const pageCount = pdfDoc.getPageCount();
      console.log(`Number of pages: ${pageCount}`);
      return pageCount;
    } catch (error) {
      console.error("Error reading PDF file:", error);
      throw error;
    }
  } else {
    console.error("Please upload a valid PDF file.");
    return null;
  }
};

export const checkAllValues = (nestedArray) => {
  const flattenedArray = nestedArray.flat();

  // Filter out empty values
  const filteredArray = flattenedArray.filter(
    (value) => value !== undefined && value !== null
  );

  // Check if all values are true or all values are false
  const areAllTrue = filteredArray.every((value) => value === true);

  return areAllTrue;
};

export const EditorImageConfig = {
  plugins: {
    plugin_crop,
    plugin_finetune,
    plugin_filter,
    plugin_annotate,
    plugin_decorate,
    plugin_sticker,
    plugin_fill,
    plugin_redact,
    plugin_frame,
  },

  DeLocale: {
    ...LocaleCoreDe,
    ...LocaleCropDe,
    ...LocaleFinetuneDe,
    ...LocaleFilterDe,
    ...LocaleAnnotateDe,
    ...LocaleMarkupEditorDe,
    ...LocaleStickerDe,
    ...LocaleFillDe,
    ...LocaleRedactDe,
    ...LocaleDecorateDe,
    ...LocaleTrimDe,
    ...LocaleResizeDe,
    ...LocaleFrameDe,
  },
  EnLocale: {
    ...LocaleCoreEn,
    ...LocaleCropEn,
    ...LocaleFinetuneEn,
    ...LocaleFilterEn,
    ...LocaleAnnotateEn,
    ...LocaleMarkupEditorEn,
    ...LocaleStickerEn,
    ...LocaleFillEn,
    ...LocaleDecorateEn,
    ...LocaleRedactEn,
    ...LocaleTrimEn,
    ...LocaleResizeEn,
    ...LocaleFrameEn,
  },

  Stickers: [
    { src: "😂", alt: "Face with Tears of Joy" },
    { src: "❤️", alt: "Red Heart" },
    { src: "🤣", alt: "Rolling on the Floor Laughing" },
    { src: "😍", alt: "Smiling Face with Heart-Eyes" },
    { src: "🚀", alt: "Rocket" },
    { src: "🙏", alt: "Folded Hands (Thank You/Praying)" },
    { src: "😊", alt: "Smiling Face with Smiling Eyes" },
    { src: "🔥", alt: "Fire" },
    { src: "😎", alt: "Smiling Face with Sunglasses" },
    { src: "💔", alt: "Broken Heart" },
    { src: "💕", alt: "Two Hearts" },
    { src: "👏", alt: "Clapping Hands" },
    { src: "💯", alt: "Hundred Points" },
    { src: "😅", alt: "Grinning Face with Sweat" },
    { src: "✨", alt: "Sparkles" },
    { src: "😢", alt: "Crying Face" },
    { src: "👍", alt: "Thumbs Up" },
    { src: "😘", alt: "Face Blowing a Kiss" },
    { src: "🎉", alt: "Party Popper" },
    { src: "🤔", alt: "Thinking Face" },
  ],
};

// export const getVideoThumbnail = (file) => {
//   return new Promise((resolve, reject) => {
//     const videoSrc = URL.createObjectURL(file);
//     const generator = new VideoThumbnailGenerator(videoSrc);
//     generator.getThumbnail().then(({ thumbnail }) => {
//       resolve(thumbnail);
//     });
//   });
// };
